import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useMenuBuilder } from '../../context/MenuBuilderContext';
import { cn } from '../../lib/utils';

export const RichMenuCanvas = ({ className }) => {
  const canvasRef = useRef(null);
  const {
    menuData,
    selectedAreaIndex,
    isDrawing,
    drawStart,
    setIsDrawing,
    setDrawStart,
    addArea,
    selectArea,
    updateArea
  } = useMenuBuilder();

  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [drawingRect, setDrawingRect] = useState(null);
  const [dragState, setDragState] = useState(null);

  const isCompact = menuData.size.height === 843;
  const aspectRatio = menuData.size.width / menuData.size.height;

  // Calculate scale factor
  const getScale = useCallback(() => {
    if (!canvasSize.width) return 1;
    return canvasSize.width / menuData.size.width;
  }, [canvasSize.width, menuData.size.width]);

  // Convert pixel coordinates to canvas coordinates
  const toCanvasCoords = useCallback((clientX, clientY) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    const scale = getScale();
    return {
      x: Math.round((clientX - rect.left) / scale),
      y: Math.round((clientY - rect.top) / scale)
    };
  }, [getScale]);

  // Update canvas size on resize
  useEffect(() => {
    const updateSize = () => {
      if (canvasRef.current) {
        const { width, height } = canvasRef.current.getBoundingClientRect();
        setCanvasSize({ width, height });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Handle mouse down - start drawing or select area
  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    
    const coords = toCanvasCoords(e.clientX, e.clientY);
    
    // Check if clicking on existing area
    const clickedAreaIndex = menuData.areas.findIndex(area => {
      const { x, y, width, height } = area.bounds;
      return coords.x >= x && coords.x <= x + width &&
             coords.y >= y && coords.y <= y + height;
    });

    if (clickedAreaIndex >= 0) {
      selectArea(clickedAreaIndex);
      // Start dragging
      setDragState({
        areaIndex: clickedAreaIndex,
        startCoords: coords,
        startBounds: { ...menuData.areas[clickedAreaIndex].bounds }
      });
    } else {
      selectArea(null);
      setIsDrawing(true);
      setDrawStart(coords);
      setDrawingRect({ x: coords.x, y: coords.y, width: 0, height: 0 });
    }
  };

  // Handle mouse move - drawing or dragging
  const handleMouseMove = (e) => {
    const coords = toCanvasCoords(e.clientX, e.clientY);

    if (isDrawing && drawStart) {
      const x = Math.min(drawStart.x, coords.x);
      const y = Math.min(drawStart.y, coords.y);
      const width = Math.abs(coords.x - drawStart.x);
      const height = Math.abs(coords.y - drawStart.y);
      
      // Clamp to canvas bounds
      const clampedWidth = Math.min(width, menuData.size.width - x);
      const clampedHeight = Math.min(height, menuData.size.height - y);
      
      setDrawingRect({ x, y, width: clampedWidth, height: clampedHeight });
    } else if (dragState) {
      const dx = coords.x - dragState.startCoords.x;
      const dy = coords.y - dragState.startCoords.y;
      
      let newX = dragState.startBounds.x + dx;
      let newY = dragState.startBounds.y + dy;
      
      // Clamp to canvas bounds
      newX = Math.max(0, Math.min(newX, menuData.size.width - dragState.startBounds.width));
      newY = Math.max(0, Math.min(newY, menuData.size.height - dragState.startBounds.height));
      
      updateArea(dragState.areaIndex, {
        bounds: {
          ...dragState.startBounds,
          x: Math.round(newX),
          y: Math.round(newY)
        }
      });
    }
  };

  // Handle mouse up - finish drawing or dragging
  const handleMouseUp = () => {
    if (isDrawing && drawingRect && drawingRect.width > 20 && drawingRect.height > 20) {
      addArea({
        bounds: {
          x: Math.round(drawingRect.x),
          y: Math.round(drawingRect.y),
          width: Math.round(drawingRect.width),
          height: Math.round(drawingRect.height)
        },
        action: {
          type: 'message',
          text: `Button ${menuData.areas.length + 1}`
        }
      });
    }

    setIsDrawing(false);
    setDrawStart(null);
    setDrawingRect(null);
    setDragState(null);
  };

  const scale = getScale();

  const handleTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    handleMouseDown({ button: 0, clientX: t.clientX, clientY: t.clientY });
  };

  const handleTouchMove = (e) => {
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    handleMouseMove({ clientX: t.clientX, clientY: t.clientY });
  };

  return (
    <div
      className={cn("relative select-none", className)}
      data-testid="rich-menu-canvas"
    >
      <div
        ref={canvasRef}
        className={cn(
          "rich-menu-canvas canvas-grid w-full relative",
          isCompact ? "aspect-compact" : "aspect-full"
        )}
        style={{ aspectRatio, touchAction: 'none' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleMouseUp}
        onTouchCancel={handleMouseUp}
      >
        {/* Background image if exists */}
        {menuData.imageUrl && (
          <img
            src={menuData.imageUrl}
            alt="Menu background"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            data-testid="canvas-image"
          />
        )}

        {/* Existing areas */}
        {menuData.areas.map((area, index) => (
          <div
            key={index}
            className={cn(
              "canvas-area",
              selectedAreaIndex === index && "selected"
            )}
            style={{
              left: area.bounds.x * scale,
              top: area.bounds.y * scale,
              width: area.bounds.width * scale,
              height: area.bounds.height * scale
            }}
            onClick={(e) => {
              e.stopPropagation();
              selectArea(index);
            }}
            data-testid={`canvas-area-${index}`}
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="font-mono text-xs text-white/70 bg-black/40 px-2 py-1 rounded">
                {index + 1}
              </span>
            </div>
          </div>
        ))}

        {/* Drawing rectangle */}
        {drawingRect && (
          <div
            className="canvas-area drawing"
            style={{
              left: drawingRect.x * scale,
              top: drawingRect.y * scale,
              width: drawingRect.width * scale,
              height: drawingRect.height * scale
            }}
          />
        )}

        {/* Empty state */}
        {!menuData.imageUrl && menuData.areas.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <p className="text-[#52525B] text-sm">Click and drag to create tap areas</p>
              <p className="text-[#3F3F46] text-xs mt-1">or upload an image first</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RichMenuCanvas;
